import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Calendar, MapPin, Ticket, ArrowRight, Loader2, PartyPopper } from 'lucide-react';
import { format } from 'date-fns';
import confetti from 'canvas-confetti';

interface PaymentDetails {
  event_title: string;
  event_date: string;
  event_venue: string;
  ticket_type: string;
  quantity: number;
  total_amount: number;
  currency_symbol: string;
}

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [isLoading, setIsLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setIsLoading(false);
    }
    
    // Trigger confetti on mount
    triggerConfetti();
  }, [sessionId]);

  const triggerConfetti = () => {
    // Fire confetti from both sides
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        colors: ['#8B5CF6', '#EC4899', '#F59E0B']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        colors: ['#8B5CF6', '#EC4899', '#F59E0B']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const verifyPayment = async () => {
    try {
      const response = await api.verifyPayment(sessionId!);
      if (response.success) {
        setPaymentDetails(response.details);
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-gray-400">Confirming your purchase...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-lg w-full glass border-white/10 overflow-hidden">
        <CardContent className="p-8 text-center space-y-6">
          {/* Success Icon */}
          <div className="relative">
            <div className="w-24 h-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-14 w-14 text-green-500" />
            </div>
            <PartyPopper className="h-8 w-8 text-yellow-500 absolute top-0 right-1/4 animate-bounce" />
          </div>

          {/* Thank You Message */}
          <div className="space-y-2">
            <h1 className="text-3xl font-heading font-bold text-white">
              Thank You! 🎉
            </h1>
            <p className="text-xl text-primary font-semibold">
              Your purchase was successful!
            </p>
            <p className="text-gray-300 text-lg mt-4">
              Can't wait to see you at the event! 
            </p>
            <p className="text-gray-400">
              Get ready for an amazing experience!
            </p>
          </div>

          {/* Event Details */}
          {paymentDetails && (
            <div className="bg-white/5 rounded-xl p-6 space-y-4 text-left border border-white/10">
              <h3 className="font-semibold text-white text-lg">{paymentDetails.event_title}</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-3 text-gray-300">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span>{format(new Date(paymentDetails.event_date), 'EEEE, MMMM d, yyyy • h:mm a')}</span>
                </div>
                
                <div className="flex items-center space-x-3 text-gray-300">
                  <MapPin className="h-5 w-5 text-primary" />
                  <span>{paymentDetails.event_venue}</span>
                </div>
                
                <div className="flex items-center space-x-3 text-gray-300">
                  <Ticket className="h-5 w-5 text-primary" />
                  <span>{paymentDetails.quantity}x {paymentDetails.ticket_type}</span>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                <span className="text-gray-400">Total Paid</span>
                <span className="text-xl font-bold text-primary">
                  {paymentDetails.currency_symbol}{paymentDetails.total_amount.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Confirmation Note */}
          <p className="text-sm text-gray-500">
            A confirmation email has been sent to your registered email address.
            Your tickets are also available in your account.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              onClick={() => navigate('/tickets')} 
              className="flex-1 shadow-glow"
              data-testid="view-tickets-btn"
            >
              <Ticket className="h-4 w-4 mr-2" />
              View My Tickets
            </Button>
            <Button 
              onClick={() => navigate('/discover')} 
              variant="outline"
              className="flex-1 border-white/20 hover:bg-white/5"
              data-testid="discover-more-btn"
            >
              Discover More Events
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
